import { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthProvider";
import {
  UserIcon,
  BriefcaseIcon,
  HomeIcon,
  CurrencyEuroIcon,
  AcademicCapIcon,
  ClipboardIcon,
  XMarkIcon,
  EnvelopeIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  ChartBarIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import axios from "axios";

const BACKEND_URL =
  window.location.hostname === "localhost"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PROD;

export default function FamilleAudit({ famille, open, onClose, onSave }) {
  const [step, setStep] = useState(1);
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    // ---- INFOS DE BASE ----
    type: "Personne Physique",
    statut: "Lead",
    referent: "Non assigné",
  
    // ---- IDENTITÉ ----
    civilite: "",
    nom: "",
    prenom: "",
    dateNaissance: "",
    lieuNaissance: "",
    nationalite: "",
    situationMatrimoniale: "",
    regimeMatrimonial: "",
    enfants: 0,
    agesEnfants: [],
    enveloppe: "",
    besoins: "",
    commentaire: "",
  
    // ---- COORDONNÉES ----
    adresse: "",
    ville: "",
    codePostal: "",
    telephone: "",
    email: "",
    linkedin: "",
  
    // ---- ACTIVITÉ & REVENUS ----
    profession: "",
    entreprise: "",
    anciennete: "",
    typeContrat: "",
    regime: "",
    revenusBrutsAnnuels: 0,
    revenusNetsAnnuels: 0,
    revenuFixe: 0,
    revenuVariable: 0,
    retraite: 0,
    pensionVersee: 0,
    pensionRecue: 0,
    dividendes: 0,
    beneficeDeficitFoncier: 0,
  
    // ---- IMPÔTS ----
    impots: {
      revenuImposable: 0,
      revenuImposableGlobal: 0,
      nombreParts: 0,
      tmi: 0,
      impotsAvantReduction: 0,
      impotsApresReduction: 0,
      impotsPayesN1: 0,
      reductions: {
        salarieDomicile: false,
        donsOeuvres: false,
        defiscalisation: false,
        divers: false,
      },
      remarques: "",
    },
  
    // ---- RÉSIDENCE PRINCIPALE ----
    residencePrincipale: {
      estProprietaire: false,
      regime: "",
      surface: 0,
      valeurActuelle: 0,
      capitalRestantDu: 0,
      dateAcquisition: "",
      montantAcquisition: 0,
      duree: 0,
      taux: 0,
      typeTaux: "",
      mensualites: 0,
      valeurNette: 0,
      locataire: {
        loyerMensuel: 0,
        surfaceM2: 0,
        colocation: false,
        details: {
          montantLoyerTotal: 0,
          cautionSolidaire: false,
          statutLocataires: "",
        },
      },
    },
  
    // ---- AUTRES BIENS IMMOBILIERS ----
    residenceSecondaire: {
      possede: false,
      typeBien: "",
      valeurEstimee: 0,
      lieu: "",
      capitalRestantDu: 0,
    },
  
    investissementLocatif: {
      possede: false,
      nombreBiens: 0,
      investissements: [],
    },
  
    // ---- ÉPARGNE ET PLACEMENTS ----
    liquidites: {
      compteCourant: 0,
      livrets: 0,
      pelCel: 0,
      autres: 0,
      percoPerp: 0,
    },
  
    placements: {
      assuranceVie: [],
      pea: 0,
      pee: 0,
      compteTitre: 0,
    },
  
    // ---- CRÉDITS COURTS TERMES ----
    creditsCourtsTermes: [],
  
    // ---- INDICATEURS FINANCIERS ----
    chargesMensuelles: 0,
    tauxEndettement: 0,
    capitalDisponible: 0,
    revenuNetMensuel: 0,
    epargneMensuelle: 0,
  
    // ---- OBJECTIFS ET PROJETS ----
    projets: {
      personnel: [],
      professionnel: [],
    },
    objectifs: [],
  
    // ---- PROFIL RISQUE ET CONNAISSANCES ----
    connaissancesFinancieres: "",
    connaissancesJuridiquesFiscales: "",
    appetenceRisque: "",
  
    // ---- VALIDATION & SIGNATURE ----
    dateAudit: new Date().toISOString().split("T")[0],
    auditeur: user?.Nom || "Non assigné",
    signature: false,
  });  

  useEffect(() => {
    if (famille) {
      setFormData((prev) => ({
        ...prev,
        ...famille,
        dateAudit: new Date().toISOString().split("T")[0],
        auditeur: user?.Nom || "Non assigné",
      }));
    }
  }, [famille, user]); 

  const steps = [
    { label: "Identité", icon: <UserIcon className="w-5 h-5" /> },
    { label: "Coordonnées", icon: <EnvelopeIcon className="w-5 h-5" /> },
    { label: "Activité & Revenus", icon: <BriefcaseIcon className="w-5 h-5" /> },
    { label: "Impôts", icon: <BanknotesIcon className="w-5 h-5" /> },
    { label: "Résidence principale", icon: <HomeIcon className="w-5 h-5" /> },
    { label: "Biens immobiliers", icon: <BuildingOffice2Icon className="w-5 h-5" /> },
    { label: "Épargne & Placements", icon: <CurrencyEuroIcon className="w-5 h-5" /> },
    { label: "Crédits", icon: <DocumentTextIcon className="w-5 h-5" /> },
    { label: "Indicateurs", icon: <ChartBarIcon className="w-5 h-5" /> },
    { label: "Objectifs", icon: <AcademicCapIcon className="w-5 h-5" /> },
    { label: "Profil", icon: <ClipboardIcon className="w-5 h-5" /> },
    { label: "Validation", icon: <CheckCircleIcon className="w-5 h-5" /> },
  ];  

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNestedChange = (e, parentKey) => {
    const { name, value, type } = e.target;
    const finalValue = type === "number" ? Number(value) : value;
  
    setFormData((prev) => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [name]: finalValue,
      },
    }));
  };  

  const handleArrayChange = (e, index, arrayKey) => {
    const { name, value, type } = e.target;
    const finalValue = type === "number" ? Number(value) : value;
  
    setFormData((prev) => {
      const updatedArray = [...prev[arrayKey]];
      updatedArray[index] = { ...updatedArray[index], [name]: finalValue };
      return { ...prev, [arrayKey]: updatedArray };
    });
  };  

  const handleSubmit = async () => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/familles/${famille._id}`,
        formData
      );
      alert("Audit patrimonial enregistré avec succès !");
      onClose(); // Ferme la modale
      window.location.reload(); // 🔄 Recharge la page
    } catch (error) {
      console.error("Erreur lors de l'enregistrement :", error);
      alert("Erreur lors de l'enregistrement de l'audit.");
    }
  };  

  const blocFields = {
    1: (
        <>
          <label className="block text-sm font-medium">Civilité</label>
          <select
            name="civilite"
            value={formData.civilite}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          >
            <option value="">Sélectionner</option>
            <option value="Monsieur">Monsieur</option>
            <option value="Madame">Madame</option>
            <option value="Autre">Autre</option>
          </select>
      
          <label className="block text-sm font-medium">Nom</label>
          <input
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Prénom</label>
          <input
            name="prenom"
            value={formData.prenom}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Date de naissance</label>
          <input
            type="date"
            name="dateNaissance"
            value={formData.dateNaissance}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Lieu de naissance</label>
          <input
            name="lieuNaissance"
            value={formData.lieuNaissance}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Nationalité</label>
          <input
            name="nationalite"
            value={formData.nationalite}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Situation matrimoniale</label>
          <input
            name="situationMatrimoniale"
            value={formData.situationMatrimoniale}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Régime matrimonial</label>
          <input
            name="regimeMatrimonial"
            value={formData.regimeMatrimonial}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Nombre d'enfants</label>
          <input
            type="number"
            name="enfants"
            value={formData.enfants}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Âges des enfants (séparés par des virgules)</label>
          <input
            name="agesEnfants"
            value={formData.agesEnfants.join(", ")}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                agesEnfants: e.target.value
                  .split(",")
                  .map((age) => parseInt(age.trim()))
                  .filter((n) => !isNaN(n)),
              }))
            }
            placeholder="Ex: 5, 10, 14"
            className="w-full border rounded px-3 py-2 mb-2"
          />
        </>
    ),      
    2: (
        <>
          <label className="block text-sm font-medium">Adresse</label>
          <input
            name="adresse"
            value={formData.adresse}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Ville</label>
          <input
            name="ville"
            value={formData.ville}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Code Postal</label>
          <input
            name="codePostal"
            value={formData.codePostal}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Téléphone</label>
          <input
            name="telephone"
            value={formData.telephone}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Profil LinkedIn</label>
          <input
            name="linkedin"
            value={formData.linkedin}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
        </>
      ),      
      3: (
        <>
          <label className="block text-sm font-medium">Profession</label>
          <input
            name="profession"
            value={formData.profession}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Entreprise</label>
          <input
            name="entreprise"
            value={formData.entreprise}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Ancienneté</label>
          <input
            name="anciennete"
            value={formData.anciennete}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Type de contrat</label>
          <input
            name="typeContrat"
            value={formData.typeContrat}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Régime</label>
          <input
            name="regime"
            value={formData.regime}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Revenus bruts annuels (€)</label>
          <input
            type="number"
            name="revenusBrutsAnnuels"
            value={formData.revenusBrutsAnnuels}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Revenus nets annuels (€)</label>
          <input
            type="number"
            name="revenusNetsAnnuels"
            value={formData.revenusNetsAnnuels}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Revenu fixe (€)</label>
          <input
            type="number"
            name="revenuFixe"
            value={formData.revenuFixe}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Revenu variable (€)</label>
          <input
            type="number"
            name="revenuVariable"
            value={formData.revenuVariable}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Retraite (€)</label>
          <input
            type="number"
            name="retraite"
            value={formData.retraite}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Pension versée (€)</label>
          <input
            type="number"
            name="pensionVersee"
            value={formData.pensionVersee}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Pension reçue (€)</label>
          <input
            type="number"
            name="pensionRecue"
            value={formData.pensionRecue}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Dividendes (€)</label>
          <input
            type="number"
            name="dividendes"
            value={formData.dividendes}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Bénéfice / déficit foncier (€)</label>
          <input
            type="number"
            name="beneficeDeficitFoncier"
            value={formData.beneficeDeficitFoncier}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
        </>
      ),          
      4: (
        <>
          <label className="block text-sm font-medium">Revenu imposable (€)</label>
          <input
            type="number"
            name="revenuImposable"
            value={formData.impots.revenuImposable || ""}
            onChange={(e) => handleNestedChange(e, "impots")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Revenu imposable global (€)</label>
          <input
            type="number"
            name="revenuImposableGlobal"
            value={formData.impots.revenuImposableGlobal || ""}
            onChange={(e) => handleNestedChange(e, "impots")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Nombre de parts</label>
          <input
            type="number"
            name="nombreParts"
            value={formData.impots.nombreParts || ""}
            onChange={(e) => handleNestedChange(e, "impots")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">TMI (%)</label>
          <input
            type="number"
            name="tmi"
            value={formData.impots.tmi || ""}
            onChange={(e) => handleNestedChange(e, "impots")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Impôts avant réduction (€)</label>
          <input
            type="number"
            name="impotsAvantReduction"
            value={formData.impots.impotsAvantReduction || ""}
            onChange={(e) => handleNestedChange(e, "impots")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Impôts après réduction (€)</label>
          <input
            type="number"
            name="impotsApresReduction"
            value={formData.impots.impotsApresReduction || ""}
            onChange={(e) => handleNestedChange(e, "impots")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Impôts payés N-1 (€)</label>
          <input
            type="number"
            name="impotsPayesN1"
            value={formData.impots.impotsPayesN1 || ""}
            onChange={(e) => handleNestedChange(e, "impots")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <div className="mt-4">
            <p className="text-sm font-medium mb-2">Réductions fiscales</p>
            <div className="space-y-2 pl-2">
              {[
                { key: "salarieDomicile", label: "Salarié à domicile" },
                { key: "donsOeuvres", label: "Dons aux œuvres" },
                { key: "defiscalisation", label: "Défiscalisation" },
                { key: "divers", label: "Autres réductions" },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name={key}
                    checked={formData.impots.reductions?.[key] || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        impots: {
                          ...prev.impots,
                          reductions: {
                            ...prev.impots.reductions,
                            [key]: checked,
                          },
                        },
                      }));
                    }}
                    className="h-4 w-4"
                  />
                  <label className="text-sm">{label}</label>
                </div>
              ))}
            </div>
          </div>
      
          <label className="block text-sm font-medium mt-4">Remarques</label>
          <textarea
            name="remarques"
            value={formData.impots.remarques || ""}
            onChange={(e) => handleNestedChange(e, "impots")}
            className="w-full border rounded px-3 py-2"
            rows={2}
          />
        </>
      ),            
      5: (
        <>
          <label className="block text-sm font-medium">Est propriétaire ?</label>
          <select
            name="estProprietaire"
            value={formData.residencePrincipale.estProprietaire ? "true" : "false"}
            onChange={(e) => handleNestedChange(e, "residencePrincipale")}
            className="w-full border rounded px-3 py-2 mb-2"
          >
            <option value="">Sélectionner</option>
            <option value="true">Oui</option>
            <option value="false">Non</option>
          </select>
      
          <label className="block text-sm font-medium">Régime</label>
          <input
            name="regime"
            value={formData.residencePrincipale.regime || ""}
            onChange={(e) => handleNestedChange(e, "residencePrincipale")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Surface (m²)</label>
          <input
            type="number"
            name="surface"
            value={formData.residencePrincipale.surface || ""}
            onChange={(e) => handleNestedChange(e, "residencePrincipale")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Valeur actuelle (€)</label>
          <input
            type="number"
            name="valeurActuelle"
            value={formData.residencePrincipale.valeurActuelle || ""}
            onChange={(e) => handleNestedChange(e, "residencePrincipale")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Capital restant dû (€)</label>
          <input
            type="number"
            name="capitalRestantDu"
            value={formData.residencePrincipale.capitalRestantDu || ""}
            onChange={(e) => handleNestedChange(e, "residencePrincipale")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Date d'acquisition</label>
          <input
            type="date"
            name="dateAcquisition"
            value={formData.residencePrincipale.dateAcquisition || ""}
            onChange={(e) => handleNestedChange(e, "residencePrincipale")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Montant d'acquisition (€)</label>
          <input
            type="number"
            name="montantAcquisition"
            value={formData.residencePrincipale.montantAcquisition || ""}
            onChange={(e) => handleNestedChange(e, "residencePrincipale")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Durée du crédit (années)</label>
          <input
            type="number"
            name="duree"
            value={formData.residencePrincipale.duree || ""}
            onChange={(e) => handleNestedChange(e, "residencePrincipale")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Taux d'intérêt (%)</label>
          <input
            type="number"
            name="taux"
            value={formData.residencePrincipale.taux || ""}
            onChange={(e) => handleNestedChange(e, "residencePrincipale")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Type de taux</label>
          <input
            name="typeTaux"
            value={formData.residencePrincipale.typeTaux || ""}
            onChange={(e) => handleNestedChange(e, "residencePrincipale")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Mensualités (€)</label>
          <input
            type="number"
            name="mensualites"
            value={formData.residencePrincipale.mensualites || ""}
            onChange={(e) => handleNestedChange(e, "residencePrincipale")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
        </>
      ),      
      6: (
        <>
          {/* 🏡 Résidence Secondaire */}
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Résidence secondaire</h3>
          <label className="block text-sm font-medium">Possède une résidence secondaire ?</label>
          <select
            name="possede"
            value={formData.residenceSecondaire.possede ? "true" : "false"}
            onChange={(e) => handleNestedChange(e, "residenceSecondaire")}
            className="w-full border rounded px-3 py-2 mb-2"
          >
            <option value="">Sélectionner</option>
            <option value="true">Oui</option>
            <option value="false">Non</option>
          </select>
      
          <label className="block text-sm font-medium">Type de bien</label>
          <input
            name="typeBien"
            value={formData.residenceSecondaire.typeBien || ""}
            onChange={(e) => handleNestedChange(e, "residenceSecondaire")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Valeur estimée (€)</label>
          <input
            type="number"
            name="valeurEstimee"
            value={formData.residenceSecondaire.valeurEstimee || ""}
            onChange={(e) => handleNestedChange(e, "residenceSecondaire")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Lieu</label>
          <input
            name="lieu"
            value={formData.residenceSecondaire.lieu || ""}
            onChange={(e) => handleNestedChange(e, "residenceSecondaire")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Capital restant dû (€)</label>
          <input
            type="number"
            name="capitalRestantDu"
            value={formData.residenceSecondaire.capitalRestantDu || ""}
            onChange={(e) => handleNestedChange(e, "residenceSecondaire")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          {/* 🏢 Investissement Locatif */}
          <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-2">Investissement locatif</h3>
          <label className="block text-sm font-medium">Possède un investissement locatif ?</label>
          <select
            name="possede"
            value={formData.investissementLocatif.possede ? "true" : "false"}
            onChange={(e) => handleNestedChange(e, "investissementLocatif")}
            className="w-full border rounded px-3 py-2 mb-2"
          >
            <option value="">Sélectionner</option>
            <option value="true">Oui</option>
            <option value="false">Non</option>
          </select>
      
          <label className="block text-sm font-medium">Nombre de biens</label>
          <input
            type="number"
            name="nombreBiens"
            value={formData.investissementLocatif.nombreBiens || 0}
            onChange={(e) => handleNestedChange(e, "investissementLocatif")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
        </>
      ),
      7: (
        <>
          {/* 💰 Liquidités */}
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Liquidités</h3>
      
          <label className="block text-sm font-medium">Compte courant (€)</label>
          <input
            type="number"
            name="compteCourant"
            value={formData.liquidites.compteCourant}
            onChange={(e) => handleNestedChange(e, "liquidites")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Livrets (A, LDD...) (€)</label>
          <input
            type="number"
            name="livrets"
            value={formData.liquidites.livrets}
            onChange={(e) => handleNestedChange(e, "liquidites")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">PEL / CEL (€)</label>
          <input
            type="number"
            name="pelCel"
            value={formData.liquidites.pelCel}
            onChange={(e) => handleNestedChange(e, "liquidites")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Autres (€)</label>
          <input
            type="number"
            name="autres"
            value={formData.liquidites.autres}
            onChange={(e) => handleNestedChange(e, "liquidites")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">PERCO / PERP (€)</label>
          <input
            type="number"
            name="percoPerp"
            value={formData.liquidites.percoPerp}
            onChange={(e) => handleNestedChange(e, "liquidites")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          {/* 📈 Placements */}
          <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-2">Placements</h3>
      
          <label className="block text-sm font-medium">PEA (€)</label>
          <input
            type="number"
            name="pea"
            value={formData.placements.pea}
            onChange={(e) => handleNestedChange(e, "placements")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">PEE (€)</label>
          <input
            type="number"
            name="pee"
            value={formData.placements.pee}
            onChange={(e) => handleNestedChange(e, "placements")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Compte-titres (€)</label>
          <input
            type="number"
            name="compteTitre"
            value={formData.placements.compteTitre}
            onChange={(e) => handleNestedChange(e, "placements")}
            className="w-full border rounded px-3 py-2 mb-2"
          />
        </>
      ),
      8: (
        <>
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Crédits courts termes</h3>
      
          {formData.creditsCourtsTermes.length > 0 &&
            formData.creditsCourtsTermes.map((credit, index) => (
              <div
                key={index}
                className="border p-4 rounded-md mb-4 bg-gray-50 relative"
              >
                <label className="block text-sm font-medium">Désignation</label>
                <input
                  name="designation"
                  value={credit.designation}
                  onChange={(e) => handleArrayChange(e, index, "creditsCourtsTermes")}
                  className="w-full border rounded px-3 py-2 mb-2"
                />
      
                <label className="block text-sm font-medium">Capital emprunté (€)</label>
                <input
                  type="number"
                  name="capitalEmprunte"
                  value={credit.capitalEmprunte}
                  onChange={(e) => handleArrayChange(e, index, "creditsCourtsTermes")}
                  className="w-full border rounded px-3 py-2 mb-2"
                />
      
                <label className="block text-sm font-medium">Mensualité (€)</label>
                <input
                  type="number"
                  name="mensualite"
                  value={credit.mensualite}
                  onChange={(e) => handleArrayChange(e, index, "creditsCourtsTermes")}
                  className="w-full border rounded px-3 py-2 mb-2"
                />
      
                <label className="block text-sm font-medium">Date de fin</label>
                <input
                  type="date"
                  name="dateFin"
                  value={credit.dateFin}
                  onChange={(e) => handleArrayChange(e, index, "creditsCourtsTermes")}
                  className="w-full border rounded px-3 py-2"
                />
      
                <button
                  type="button"
                  className="absolute top-2 right-2 text-red-500 text-sm"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      creditsCourtsTermes: prev.creditsCourtsTermes.filter((_, i) => i !== index),
                    }))
                  }
                >
                  ✕
                </button>
              </div>
            ))}
      
          <button
            type="button"
            onClick={() =>
              setFormData((prev) => ({
                ...prev,
                creditsCourtsTermes: [
                  ...prev.creditsCourtsTermes,
                  {
                    designation: "",
                    capitalEmprunte: 0,
                    mensualite: 0,
                    dateFin: "",
                  },
                ],
              }))
            }
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            + Ajouter un crédit
          </button>
        </>
      ),
      9: (
        <>
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Indicateurs financiers</h3>
      
          <label className="block text-sm font-medium">Charges mensuelles (€)</label>
          <input
            type="number"
            name="chargesMensuelles"
            value={formData.chargesMensuelles}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Taux d’endettement (%)</label>
          <input
            type="number"
            name="tauxEndettement"
            value={formData.tauxEndettement}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Capital disponible (€)</label>
          <input
            type="number"
            name="capitalDisponible"
            value={formData.capitalDisponible}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Revenu net mensuel (€)</label>
          <input
            type="number"
            name="revenuNetMensuel"
            value={formData.revenuNetMensuel}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Épargne mensuelle (€)</label>
          <input
            type="number"
            name="epargneMensuelle"
            value={formData.epargneMensuelle}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
          />
        </>
      ),
      10: (
        <>
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Objectifs et projets</h3>
      
          <label className="block text-sm font-medium">Objectifs</label>
          <textarea
            name="objectifs"
            value={formData.objectifs}
            onChange={handleChange}
            rows={2}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Projets personnels (séparés par une virgule)</label>
          <input
            type="text"
            name="projets.personnel"
            value={formData.projets.personnel.join(", ")}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                projets: {
                  ...prev.projets,
                  personnel: e.target.value.split(",").map((s) => s.trim()),
                },
              }))
            }
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Projets professionnels (séparés par une virgule)</label>
          <input
            type="text"
            name="projets.professionnel"
            value={formData.projets.professionnel.join(", ")}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                projets: {
                  ...prev.projets,
                  professionnel: e.target.value.split(",").map((s) => s.trim()),
                },
              }))
            }
            className="w-full border rounded px-3 py-2"
          />
        </>
      ),
      11: (
        <>
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Profil de risque & Connaissances</h3>
      
          <label className="block text-sm font-medium">Connaissances financières</label>
          <select
            name="connaissancesFinancieres"
            value={formData.connaissancesFinancieres}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          >
            <option value="">Sélectionner</option>
            <option value="Faibles">Faibles</option>
            <option value="Moyennes">Moyennes</option>
            <option value="Élevées">Élevées</option>
          </select>
      
          <label className="block text-sm font-medium">Connaissances juridiques et fiscales</label>
          <select
            name="connaissancesJuridiquesFiscales"
            value={formData.connaissancesJuridiquesFiscales}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          >
            <option value="">Sélectionner</option>
            <option value="Faibles">Faibles</option>
            <option value="Moyennes">Moyennes</option>
            <option value="Élevées">Élevées</option>
          </select>
      
          <label className="block text-sm font-medium">Appétence au risque</label>
          <select
            name="appetenceRisque"
            value={formData.appetenceRisque}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">Sélectionner</option>
            <option value="Faible">Faible</option>
            <option value="Moyenne">Moyenne</option>
            <option value="Élevée">Élevée</option>
          </select>
        </>
      ),
      12: (
        <>
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Validation & Signature</h3>
      
          <label className="block text-sm font-medium">Date de l'audit</label>
          <input
            type="date"
            name="dateAudit"
            value={formData.dateAudit?.slice(0, 10) || ""}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <label className="block text-sm font-medium">Auditeur</label>
          <input
            name="auditeur"
            value={formData.auditeur}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mb-2"
          />
      
          <div className="flex items-center gap-2 mt-2">
            <input
              type="checkbox"
              name="signature"
              checked={formData.signature}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  signature: e.target.checked,
                }))
              }
              className="h-4 w-4 text-blue-600"
            />
            <label className="text-sm text-gray-700">Audit signé</label>
          </div>
        </>
      ),            
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="relative bg-white rounded-lg shadow-lg w-full max-w-3xl p-6">
  
          {/* Bouton de fermeture */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
  
          {/* Titre et icône */}
          <DialogTitle className="text-xl font-bold text-gray-800 mb-4 text-center flex items-center gap-2">
            {steps[step - 1].icon}
            Étape {step} : {steps[step - 1].label}
          </DialogTitle>
  
          {/* Barre de progression */}
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${(step / steps.length) * 100}%` }}
            />
          </div>
  
          {/* Champs dynamiques */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto pr-2">
            {blocFields[step]}
          </div>
  
          {/* Boutons de navigation */}
          <div className="flex justify-between mt-6">
            <button
              disabled={step === 1}
              onClick={() => setStep((s) => s - 1)}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg"
            >
              Précédent
            </button>
            {step < steps.length ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                Suivant
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
              >
                Enregistrer l’audit
              </button>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );  
}
